import { useState } from 'react';
import { useTranslation } from 'react-i18next';

import { useIsOfferLocked } from '@/hooks/use-is-offer-locked';
import { useShippingPolicy } from '@/lib/hooks/useShippingPolicy';
import { ShippingPolicy } from '@/lib/types/shipping-policy';
import { cn, formatCurrency } from '@/lib/utils';
import { storeAtom } from '@/states/store.state';
import Button from '@appchoose/button';
import { useAtomValue } from 'jotai';
import { Edit } from 'lucide-react';

import {
  PickupPointDisplay,
  getNetworkLabel,
} from './pickup-point-configurator';

interface PickupPointBlockProps {
  dealId: string;
  hubspotStartDate?: Date | null;
  disabled?: boolean;
}

export function PickupPointBlock({
  dealId,
  disabled = false,
}: PickupPointBlockProps) {
  const { t } = useTranslation();
  const store = useAtomValue(storeAtom);
  const isOfferLocked = useIsOfferLocked();
  const { shippingPolicy, handleShippingPolicyChange } =
    useShippingPolicy(dealId);
  const [configModalOpen, setConfigModalOpen] = useState(false);

  const { pickupPointEnabled, pickupPointNetwork, pickupPointDeliveryPrice } =
    shippingPolicy;

  return (
    <>
      <section
        onClick={() => !isOfferLocked && setConfigModalOpen(true)}
        className={cn(
          'flex items-center justify-between gap-4 rounded-lg border border-neutral-200 p-6 transition-colors',
          isOfferLocked
            ? 'cursor-not-allowed opacity-50'
            : !disabled
              ? 'cursor-pointer hover:bg-neutral-50'
              : ''
        )}
      >
        <div className="flex-1">
          <div className="mb-2 flex items-center gap-3">
            <h2 className="text-lg font-semibold">
              {pickupPointEnabled && pickupPointNetwork
                ? getNetworkLabel(pickupPointNetwork)
                : t('ui-v2.settings.blocks-shipping-policy.pickup-point.title')}
            </h2>
            <span
              className={cn(
                'rounded-full px-2.5 py-0.5 text-xs font-medium',
                pickupPointEnabled
                  ? 'bg-blue-100 text-blue-700'
                  : 'bg-gray-100 text-gray-700'
              )}
            >
              {pickupPointEnabled
                ? t(
                    'ui-v2.settings.blocks-shipping-policy.pickup-point.status-requested'
                  )
                : t('ui-v2.settings.blocks-shipping-policy.status-inactive')}
            </span>
          </div>

          {pickupPointEnabled && (
            <div className="space-y-1 text-sm text-muted-foreground">
              {pickupPointNetwork && pickupPointDeliveryPrice !== null && (
                <div>
                  {t(
                    'ui-v2.settings.blocks-shipping-policy.pickup-point.price'
                  )}{' '}
                  <span className="font-semibold text-foreground">
                    {formatCurrency(pickupPointDeliveryPrice, store)}
                  </span>
                </div>
              )}
            </div>
          )}

          {!pickupPointEnabled && (
            <p className="text-sm text-muted-foreground">
              {t(
                'ui-v2.settings.blocks-shipping-policy.pickup-point.no-policy'
              )}
            </p>
          )}
        </div>

        {!disabled && (
          <Button
            type="button"
            appearance="outline"
            containsIcon={true}
            onClick={(e) => {
              e.stopPropagation();
              if (!isOfferLocked) {
                setConfigModalOpen(true);
              }
            }}
            disabled={isOfferLocked}
            className="gap-2 border-0"
          >
            <Edit className="h-4 w-4" />
            {t('ui-v2.settings.blocks-shipping-policy.action-update')}
          </Button>
        )}
      </section>

      <PickupPointDisplay
        policy={shippingPolicy}
        onPolicyChange={async (newPolicy: ShippingPolicy) => {
          await handleShippingPolicyChange(newPolicy);
          setConfigModalOpen(false);
        }}
        isLocked={isOfferLocked}
        open={configModalOpen && !disabled}
        onOpenChange={setConfigModalOpen}
      />
    </>
  );
}
